import React, { useState } from "react";
import { motion } from 'framer-motion';
import userUploadImage from "../../medias/user-upload.png";
import tshirtPreviewFront10 from "../../medias/tshirt-preview-front10.png";
import tshirtPreviewFront8 from "../../medias/tshirt-preview-front8.png";
import tshirtPreviewBack10 from "../../medias/tshirt-preview-back10.png";
import tshirtPreviewBack8 from "../../medias/tshirt-preview-back8.png";
import tshirtProductImage from "../../medias/tshirt-product.png";
import websiteFooterLogo from "../../medias/website-footer-logo.png";
import { trackConfigurationChange, trackViewChange } from "../../../utils/analytics";

const ShipStationDemo = () => {
  const [mapTo, setMapTo] = useState("Item Options");
  const [surfaceNames, setSurfaceNames] = useState("never");
  const [customizationField, setCustomizationField] = useState("always");
  const [itemNames, setItemNames] = useState("always");
  const [quantity, setQuantity] = useState("never");
  const [displayPreviewImage, setDisplayPreviewImage] = useState(true);
  const [displayUserUploaded, setDisplayUserUploaded] = useState(true);
  const [activeView, setActiveView] = useState("order");

  // Sample Amazon order data
  const amazonOrder = {
    items: [
      {
        name: "Soccer Shirt, Personalized Shirt for Kids, Black",
        sku: "SHRT-SCR-BLK",
        quantity: 1,
        customizations: {
          "Front": {
            "Team Name": "Panthers"
          },
          "Back": {
            "Number": "10",
            "Name": "Smith"
          }
        }
      },
      {
        name: "Soccer Shirt, Personalized Shirt for Kids, Black",
        sku: "SHRT-SCR-BLK",
        quantity: 1,
        customizations: {
          "Front": {
            "Team Name": "Tigers"
          },
          "Back": {
            "Number": "8",
            "Name": "Smith"
          }
        }
      }
    ]
  };

  // Generate ShipStation order data based on configuration
  const generateShipStationData = () => {
    let orderNotes = "";
    let internalNotes = "";
    let itemOptions = [];
    const hasMultipleItems = amazonOrder.items.length > 1;

    // Helper function to check if a field should be included
    const shouldInclude = (setting) => {
      if (setting === "always") return true;
      if (setting === "never") return false;
      if (setting === "only when multiple items") return hasMultipleItems;
      return false;
    };

    // Process customizations based on mapping
    amazonOrder.items.forEach((item, index) => {
      // Add item name as header if enabled (only for order-level notes)
      if (shouldInclude(itemNames)) {
        if (mapTo === "Note From Buyer") {
          orderNotes += `${item.name}\n`;
        } else if (mapTo === "Internal Notes") {
          internalNotes += `${item.name}\n`;
        }
      }

      // Add quantity as header if enabled (only for order-level notes)
      if (shouldInclude(quantity)) {
        if (mapTo === "Note From Buyer") {
          orderNotes += `Quantity: ${item.quantity}\n`;
        } else if (mapTo === "Internal Notes") {
          internalNotes += `Quantity: ${item.quantity}\n`;
        }
      }

      Object.entries(item.customizations).forEach(([surface, labels]) => {
        // Add surface name as header if enabled (only for order-level notes)
        if (shouldInclude(surfaceNames)) {
          if (mapTo === "Note From Buyer") {
            orderNotes += `${surface}:\n`;
          } else if (mapTo === "Internal Notes") {
            internalNotes += `${surface}:\n`;
          }
        }
        
        Object.entries(labels).forEach(([label, value]) => {
          let line = "";
          
          // For Item Options, only show customization field labels and values
          if (mapTo === "Item Options") {
            if (shouldInclude(customizationField)) {
              itemOptions.push({ 
                itemIndex: index, 
                label: `${label}:`, 
                value: value, 
                isHeader: false 
              });
            } else {
              itemOptions.push({ 
                itemIndex: index, 
                label: "", 
                value: value, 
                isHeader: false 
              });
            }
          } else {
            // For order-level notes, include customization field if enabled
            if (shouldInclude(customizationField)) {
              line += `${label}: `;
            }
            line += value;
            
            if (mapTo === "Note From Buyer") {
              orderNotes += line + "\n";
            } else if (mapTo === "Internal Notes") {
              internalNotes += line + "\n";
            }
          }
        });
      });

      // Add empty line between items (except for the last item)
      if (index < amazonOrder.items.length - 1) {
        if (mapTo === "Note From Buyer") {
          orderNotes += "\n";
        } else if (mapTo === "Internal Notes") {
          internalNotes += "\n";
        }
      }
    });

    return {
      orderNotes: orderNotes.trim(),
      internalNotes: internalNotes.trim(),
      itemOptions: itemOptions
    };
  };

  const shipStationData = generateShipStationData();

  // Helper function to get the appropriate front preview image based on item customization
  const getFrontPreviewImage = (item) => {
    // Check if the item has a "Number" customization with value "10"
    if (item.customizations.Back && item.customizations.Back.Number === "10") {
      return tshirtPreviewFront10;
    }
    // Check if the item has a "Number" customization with value "8"
    if (item.customizations.Back && item.customizations.Back.Number === "8") {
      return tshirtPreviewFront8;
    }
    // Return null if no matching front image
    return null;
  };

  // Helper function to get the appropriate back preview image based on item customization
  const getBackPreviewImage = (item) => {
    // Check if the item has a "Number" customization with value "10"
    if (item.customizations.Back && item.customizations.Back.Number === "10") {
      return tshirtPreviewBack10;
    }
    // Check if the item has a "Number" customization with value "8"
    if (item.customizations.Back && item.customizations.Back.Number === "8") {
      return tshirtPreviewBack8;
    }
    // Return null if no matching back image
    return null;
  };

  return (
    <div className="grid lg:grid-cols-2 gap-8">
      {/* Configuration Panel */}
      <motion.div
        initial={{ opacity: 0, x: -50 }}
        whileInView={{ opacity: 1, x: 0 }}
        transition={{ duration: 0.8, delay: 0.2 }}
        viewport={{ once: true }}
        className="bg-gray-50 rounded-lg p-6"
      >
        <h3 className="text-2xl font-semibold text-gray-900 mb-6">Configuration</h3>
        
        {/* Configuration Row */}
        <div className="mb-6">
          <div className="grid grid-cols-2 gap-6">
            {/* Map Customization to */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-3">
                Map Customization to:
              </label>
              <div className="space-y-2">
                {["Item Options", "Note From Buyer", "Internal Notes"].map((option) => (
                  <label key={option} className="flex items-center">
                    <input
                      type="radio"
                      name="mapTo"
                      value={option}
                      checked={mapTo === option}
                      onChange={(e) => {
                        setMapTo(e.target.value);
                        trackConfigurationChange("shipstation", "mapTo", e.target.value);
                      }}
                      className="mr-3"
                    />
                    <span className="text-sm text-gray-700">{option}</span>
                  </label>
                ))}
              </div>
            </div>

            {/* Image Display */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-3">
                Image Display:
              </label>
              <div className="space-y-3">
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={displayPreviewImage}
                    onChange={(e) => {
                      setDisplayPreviewImage(e.target.checked);
                      trackConfigurationChange("shipstation", "displayPreviewImage", e.target.checked);
                    }}
                    className="mr-3"
                  />
                  <span className="text-sm text-gray-700">Display Preview Image</span>
                </label>
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={displayUserUploaded}
                    onChange={(e) => {
                      setDisplayUserUploaded(e.target.checked);
                      trackConfigurationChange("shipstation", "displayUserUploaded", e.target.checked);
                    }}
                    className="mr-3"
                  />
                  <span className="text-sm text-gray-700">Display User Uploaded</span>
                </label>
              </div>
            </div>
          </div>
        </div>

        {/* Include/Exclude Options */}
        <div className="space-y-4">
          <h4 className="text-lg font-medium text-gray-900">Include/Exclude:</h4>
          <p className="text-sm text-gray-600 italic">These settings don't apply when Item Options is selected.</p>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Surface names:
            </label>
            <select
              value={surfaceNames}
              onChange={(e) => {
                setSurfaceNames(e.target.value);
                trackConfigurationChange("shipstation", "surfaceNames", e.target.value);
              }}
              className="w-full p-2 border border-gray-300 rounded-md text-sm"
            >
              <option value="always">Always</option>
              <option value="never">Never</option>
              <option value="only when multiple items">Only when multiple items</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Customization field:
            </label>
            <select
              value={customizationField}
              onChange={(e) => {
                setCustomizationField(e.target.value);
                trackConfigurationChange("shipstation", "customizationField", e.target.value);
              }}
              className="w-full p-2 border border-gray-300 rounded-md text-sm"
            >
              <option value="always">Always</option>
              <option value="never">Never</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Item names:
            </label>
            <select
              value={itemNames}
              onChange={(e) => {
                setItemNames(e.target.value);
                trackConfigurationChange("shipstation", "itemNames", e.target.value);
              }}
              className="w-full p-2 border border-gray-300 rounded-md text-sm"
            >
              <option value="always">Always</option>
              <option value="never">Never</option>
              <option value="only when multiple items">Only when multiple items</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Quantity:
            </label>
            <select
              value={quantity}
              onChange={(e) => {
                setQuantity(e.target.value);
                trackConfigurationChange("shipstation", "quantity", e.target.value);
              }}
              className="w-full p-2 border border-gray-300 rounded-md text-sm"
            >
              <option value="always">Always</option>
              <option value="never">Never</option>
              <option value="only when multiple items">Only when multiple items</option>
            </select>
          </div>
        </div>
      </motion.div>

      {/* Preview Panel */}
      <motion.div
        initial={{ opacity: 0, x: 50 }}
        whileInView={{ opacity: 1, x: 0 }}
        transition={{ duration: 0.8, delay: 0.4 }}
        viewport={{ once: true }}
        className="bg-gray-50 rounded-lg"
      >
        {/* View Toggle */}
        <div className="border-b border-gray-200">
          <div className="flex">
            <button
              onClick={() => {
                setActiveView("order");
                trackViewChange("shipstation", "order");
              }}
              className={`flex-1 py-3 px-4 text-sm font-medium ${
                activeView === "order"
                  ? "text-blue-600 border-b-2 border-blue-600"
                  : "text-gray-500 hover:text-gray-700"
              }`}
            >
              ShipStation Order Mock UI
            </button>
            <button
              onClick={() => {
                setActiveView("packing");
                trackViewChange("shipstation", "packing");
              }}
              className={`flex-1 py-3 px-4 text-sm font-medium ${
                activeView === "packing"
                  ? "text-blue-600 border-b-2 border-blue-600"
                  : "text-gray-500 hover:text-gray-700"
              }`}
            >
              Mock Packing Slip
            </button>
          </div>
        </div>

        {/* Order View */}
        {activeView === "order" && (
          <div className="p-6">
            {/* ShipStation-style header */}
            <div className="mb-6">
              <h2 className="text-xl font-semibold text-gray-900">Shipment Items</h2>
            </div>

            {/* Items table */}
            <div className="border border-gray-200 rounded-lg overflow-hidden">
              {/* Table header */}
              <div className="bg-gray-50 border-b border-gray-200">
                <div className="grid grid-cols-5 gap-4 px-4 py-3 text-sm font-medium text-gray-700">
                  <div className="col-span-2">Item Name</div>
                  <div className="text-center">Image</div>
                  <div className="text-center">Quantity</div>
                  <div className="text-right">Total</div>
                </div>
              </div>

              {/* Table body */}
              <div className="bg-white">
                {amazonOrder.items.map((item, index) => (
                  <div key={index} className="border-b border-gray-100 last:border-b-0">
                    {/* Main row */}
                    <div className="grid grid-cols-5 gap-4 px-4 py-3 items-center">
                      <div className="col-span-2">
                        <button className="text-left text-blue-600 hover:text-blue-800 font-medium text-sm">
                          {item.name}
                        </button>
                        <div className="text-xs text-gray-500 mt-1">SKU: {item.sku}</div>
                      </div>
                      <div className="flex justify-center">
                        <div className="w-12 h-12 rounded flex items-center justify-center">
                          <img 
                            src={tshirtProductImage} 
                            alt={item.name}
                            className="w-12 h-12 object-cover rounded"
                          />
                        </div>
                      </div>
                      <div className="text-center">
                        <p className="text-sm font-medium">{item.quantity}</p>
                      </div>
                      <div className="text-right">
                        <span className="text-sm text-gray-600">$19.99</span>
                      </div>
                    </div>

                    {/* Expandable details row */}
                    {(mapTo === "Item Options" || displayPreviewImage || displayUserUploaded) && (
                      <div className="bg-gray-50 border-t border-gray-100">
                        <div className="px-4 py-3">
                          <div className="flex items-center mb-3">
                            <svg className="w-4 h-4 mr-2 text-gray-500" fill="currentColor" viewBox="0 0 320 512">
                              <path d="M137.4 374.6c12.5 12.5 32.8 12.5 45.3 0l128-128c9.2-9.2 11.9-22.9 6.9-34.9s-16.6-19.8-29.6-19.8L32 192c-12.9 0-24.6 7.8-29.6 19.8s-2.2 25.7 6.9 34.9l128 128z"/>
                            </svg>
                            <span className="text-xs text-gray-500 font-medium">Description</span>
                          </div>
                          <div className="flex">
                            <div className="flex-1 space-y-2">
                              {mapTo === "Item Options" && shipStationData.itemOptions
                                .filter(option => option.itemIndex === index)
                                .map((option, optIndex) => (
                                  <div key={optIndex} className="flex">
                                    {option.isHeader ? (
                                      <span className="text-sm font-semibold text-gray-700">
                                        {option.text}
                                      </span>
                                    ) : (
                                      <span className="text-sm text-gray-600">
                                        <span className="font-semibold">{option.label}</span>
                                        {option.label && " "}
                                        {option.value}
                                      </span>
                                    )}
                                  </div>
                                ))}
                            </div>
                            
                            {/* Images section */}
                            {(displayPreviewImage || displayUserUploaded) && (
                              <div className={`flex flex-row space-x-2 ${mapTo === "Item Options" ? "ml-6" : ""}`}>
                                {displayPreviewImage && (
                                  <>
                                    {/* Front Preview Image */}
                                    {getFrontPreviewImage(item) && (
                                      <div>
                                        <img 
                                          src={getFrontPreviewImage(item)} 
                                          alt="Front Preview" 
                                          className="w-24 h-24 object-cover rounded border border-gray-200"
                                        />
                                        <p className="text-xs text-gray-500 mt-1 text-center">Front</p>
                                      </div>
                                    )}
                                    {/* Back Preview Image (if available) */}
                                    {getBackPreviewImage(item) && (
                                      <div>
                                        <img 
                                          src={getBackPreviewImage(item)} 
                                          alt="Back Preview" 
                                          className="w-24 h-24 object-cover rounded border border-gray-200"
                                        />
                                        <p className="text-xs text-gray-500 mt-1 text-center">Back</p>
                                      </div>
                                    )}
                                  </>
                                )}
                                {displayUserUploaded && (
                                  <div>
                                    <img 
                                      src={userUploadImage} 
                                      alt="User Upload" 
                                      className="w-24 h-24 object-cover rounded border border-gray-200"
                                    />
                                    <p className="text-xs text-gray-500 mt-1 text-center">User Upload</p>
                                  </div>
                                )}
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>

            {/* Order Details section */}
            {(mapTo === "Note From Buyer" || mapTo === "Internal Notes") && (
              <div className="mt-6">
                <h4 className="text-lg font-semibold text-gray-900 mb-3">Order Details</h4>
                <div className="bg-white border border-gray-200 rounded-lg p-4">
                  {mapTo === "Note From Buyer" && shipStationData.orderNotes && (
                    <div className="mb-3">
                      <span className="text-sm font-medium text-gray-700">Note From Buyer:</span>
                      <p className="text-sm text-gray-800 mt-1 whitespace-pre-line">{shipStationData.orderNotes}</p>
                    </div>
                  )}
                  {mapTo === "Internal Notes" && shipStationData.internalNotes && (
                    <div className="mb-3">
                      <span className="text-sm font-medium text-gray-700">Internal Notes:</span>
                      <p className="text-sm text-gray-800 mt-1 whitespace-pre-line">{shipStationData.internalNotes}</p>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Packing Slip View */}
        {activeView === "packing" && (
          <div className="p-6">
            <div className="border border-gray-300 bg-white rounded-lg overflow-hidden">
              {/* Order Header */}
              <table className="w-full" cellSpacing="0" cellPadding="2">
                <thead>
                  <tr className="bg-gray-100">
                    <th colSpan="3" className="text-center py-3 text-lg font-bold border-b border-gray-300">
                      Packing Slip
                    </th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td colSpan="2" className="p-4">
                      <div className="text-sm font-medium">Ship To:</div>
                      <div className="text-sm font-bold mt-1">
                        <div>John Smith</div>
                        <div>123 Main Street</div>
                        <div>Anytown, CA 90210</div>
                      </div>
                    </td>
                                        <td className="text-right p-4 align-top">
                      <div className="flex justify-end">
                        <img 
                          src={websiteFooterLogo} 
                          alt="Store Logo"
                          className="mb-2 h-10"
                        />
                      </div>
                      <div className="font-bold">CustomSync</div>
                      <div className="text-sm">customsync.app</div>
                    </td>
                  </tr>
                </tbody>
              </table>
              
              <br />
              
              {/* Order Info */}
              <table className="mb-4 ml-4" cellSpacing="0" border="0">
                <tr>
                  <td className="text-left pr-4 py-0 font-medium text-xs">Order #</td>
                  <td className="py-0 text-xs">112-8877665-4433221</td>
                </tr>
                <tr>
                  <td className="text-left pr-4 py-0 font-medium text-xs">Date</td>
                  <td className="py-0 text-xs">December 15, 2024</td>
                </tr>
                <tr>
                  <td className="text-left pr-4 py-0 font-medium text-xs">User</td>
                  <td className="py-0 text-xs">admin</td>
                </tr>
                <tr>
                  <td className="text-left pr-4 py-0 font-medium text-xs">Ship Method</td>
                  <td className="py-0 text-xs font-bold">USPS Priority Mail</td>
                </tr>
              </table>
              
              {/* Items Table */}
              <table className="w-full border-t border-gray-300" cellSpacing="0">
                <thead>
                  <tr className="border-b border-gray-300 bg-gray-100">
                    <th className="text-left p-2 font-medium w-32 text-sm">Item</th>
                    <th className="text-left p-2 font-medium text-sm">Description</th>
                    <th className="text-center p-2 font-medium w-20 text-sm">Qty</th>
                    {displayPreviewImage && (
                      <th className="text-center p-2 font-medium text-sm">Preview</th>
                    )}
                    {displayUserUploaded && (
                      <th className="text-center p-2 font-medium text-sm">Upload</th>
                    )}
                  </tr>
                </thead>
                <tbody>
                  {amazonOrder.items.map((item, index) => (
                    <tr key={index} className="border-b border-gray-200">
                      <td className="p-2">
                        <div className="text-xs font-medium">{item.sku}</div>
                        <img 
                          src={tshirtProductImage} 
                          alt={item.name}
                          className="w-16 h-16 object-cover mt-1"
                        />
                      </td>
                      <td className="p-2">
                        <div className="text-sm font-medium">{item.name}</div>
                        <br />
                        {mapTo === "Item Options" && (
                          <div className="text-sm">
                            {shipStationData.itemOptions
                              .filter(option => option.itemIndex === index)
                              .map((option, optIndex) => (
                                <div key={optIndex}>
                                  {option.isHeader ? (
                                    <div className="font-semibold text-gray-700">{option.text}</div>
                                  ) : (
                                    <div className="text-gray-600">
                                      <span className="font-semibold">{option.label}</span>
                                      {option.label && " "}
                                      {option.value}
                                    </div>
                                  )}
                                </div>
                              ))}
                          </div>
                        )}
                      </td>
                      <td className="p-2 text-center font-bold text-lg">{item.quantity}</td>
                      {displayPreviewImage && (
                        <td className="p-2 text-center">
                          <div className="flex flex-row space-x-1 justify-center">
                            {/* Front Preview Image */}
                            {getFrontPreviewImage(item) && (
                              <div className="flex flex-col items-center">
                                <img 
                                  src={getFrontPreviewImage(item)} 
                                  alt="Front Preview" 
                                  className="w-25 h-25 object-cover rounded border border-gray-200"
                                />
                                <span className="text-xs text-gray-500">Front</span>
                              </div>
                            )}
                            {/* Back Preview Image */}
                            {getBackPreviewImage(item) && (
                              <div className="flex flex-col items-center">
                                <img 
                                  src={getBackPreviewImage(item)} 
                                  alt="Back Preview" 
                                  className="w-25 h-25 object-cover rounded border border-gray-200"
                                />
                                <span className="text-xs text-gray-500">Back</span>
                              </div>
                            )}
                          </div>
                        </td>
                      )}
                      {displayUserUploaded && (
                        <td className="p-2 text-center">
                          <img 
                            src={userUploadImage} 
                            alt="User Upload" 
                            className="w-16 h-16 object-cover rounded border border-gray-200"
                          />
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
              
              {/* Order Notes */}
              {(mapTo === "Note From Buyer" || mapTo === "Internal Notes") && (
                <div className="p-4 border-t border-gray-300">
                  {mapTo === "Note From Buyer" && shipStationData.orderNotes && (
                    <div>
                      <div className="font-medium text-gray-700">Note From Buyer:</div>
                      <div className="text-gray-600 whitespace-pre-line">{shipStationData.orderNotes}</div>
                    </div>
                  )}
                  {mapTo === "Internal Notes" && shipStationData.internalNotes && (
                    <div>
                      <div className="font-medium text-gray-700">Internal Notes:</div>
                      <div className="text-gray-600 whitespace-pre-line">{shipStationData.internalNotes}</div>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        )}
      </motion.div>
    </div>
  );
};

export default ShipStationDemo;
